import type { ReactElement } from "react"
import type { LucideIcon } from "lucide-react"
import {
  AlignJustify,
  Brain,
  Calendar,
  Code2,
  File,
  FileCode,
  FileText,
  GraduationCap,
  Heart,
  Image,
  Lightbulb,
  Link2,
  Mic,
  PenLine,
  Sparkles,
  Table2,
  Volume2,
  Zap,
} from "lucide-react"

export type DocumentIconInput = {
  /** frontmatter.kind (wins over document_type) */
  kind?: string | null
  /** documents.document_type / doc.type */
  type?: string | null
  filePath?: string | null
  /** e.g. `.html` from explorer metadata */
  extension?: string | null
}

export type DocumentIconOptions = {
  className?: string
  isProjectRoot?: boolean
}

export type DocumentKindIconDef = {
  icon: LucideIcon
  /** Tailwind color classes applied with size className */
  colorClass?: string
}

/** Kind/type keys -> icon. Extensions/kinds register via registerDocumentKindIcon. */
const kindIconRegistry = new Map<string, DocumentKindIconDef>()

function normalizeKey(raw: string | null | undefined): string {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
}

/** Register or override the browse/list icon for a structured document kind. */
export function registerDocumentKindIcon(kind: string, def: DocumentKindIconDef): void {
  const key = normalizeKey(kind)
  if (!key) return
  kindIconRegistry.set(key, def)
}

function seedDefaultKindIcons(): void {
  const defaults: Record<string, DocumentKindIconDef> = {
    note: { icon: FileText, colorClass: "text-sidebar-foreground/60" },
    work_spec: { icon: FileCode, colorClass: "text-blue-500" },
    voice_memo: { icon: Mic, colorClass: "text-purple-500" },
    meeting: { icon: Calendar, colorClass: "text-green-500" },
    idea: { icon: Lightbulb, colorClass: "text-yellow-500" },
    ai_suggestion: { icon: Sparkles, colorClass: "text-purple-400" },
    quiz: { icon: GraduationCap, colorClass: "text-teal-600" },
    flow: { icon: Zap, colorClass: "text-green-500" },
    skill: { icon: Sparkles, colorClass: "text-amber-500" },
    platform_skill: { icon: Sparkles, colorClass: "text-amber-500" },
    soul: { icon: Heart, colorClass: "text-rose-500" },
    soul_stack: { icon: Heart, colorClass: "text-rose-500" },
    session_memory: { icon: Brain, colorClass: "text-violet-500" },
    session_scratch: { icon: FileText, colorClass: "text-sidebar-foreground/60" },
    session_image: { icon: Image, colorClass: "text-sky-500" },
    canvas: { icon: PenLine, colorClass: "text-amber-500" },
    pointer: { icon: Link2, colorClass: "text-sky-500" },
    html: { icon: Code2, colorClass: "text-gray-500" },
    csv: { icon: Table2, colorClass: "text-emerald-600" },
    audio: { icon: Volume2, colorClass: "text-orange-600" },
    image: { icon: Image, colorClass: "text-sky-600" },
    pdf: { icon: File, colorClass: "text-red-500" },
    product_board: { icon: AlignJustify, colorClass: "text-primary" },
  }
  for (const [key, def] of Object.entries(defaults)) {
    if (!kindIconRegistry.has(key)) {
      kindIconRegistry.set(key, def)
    }
  }
}

seedDefaultKindIcons()

function resolveKey(input: DocumentIconInput): string {
  const kind = normalizeKey(input.kind)
  if (kind && kindIconRegistry.has(kind)) return kind

  const type = normalizeKey(input.type)
  if (type && kindIconRegistry.has(type)) return type

  const ext = String(input.extension || "").toLowerCase()
  if (ext === ".html" || input.filePath?.toLowerCase().endsWith(".html")) {
    return "html"
  }
  if (ext === ".csv" || input.filePath?.toLowerCase().endsWith(".csv")) {
    return "csv"
  }
  if (ext === ".mp3" || input.filePath?.toLowerCase().endsWith(".mp3")) {
    return "audio"
  }
  if (ext === ".pdf" || input.filePath?.toLowerCase().endsWith(".pdf")) {
    return "pdf"
  }
  if (
    [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"].includes(ext) ||
    /\.(png|jpe?g|gif|webp|svg)$/i.test(String(input.filePath || ""))
  ) {
    return "image"
  }

  if (input.filePath && isProjectRootFile(input.filePath)) {
    return "__project_root__"
  }

  return type || "note"
}

function renderDef(
  def: DocumentKindIconDef,
  className: string,
): ReactElement {
  const Icon = def.icon
  const color = def.colorClass || "text-muted-foreground"
  return <Icon className={`${className} ${color}`} />
}

/**
 * Canonical document icon resolver (structured-documents Rule 2 / document-iconography.mdc).
 * Precedence: frontmatter.kind -> document_type -> extension -> project-root heuristic.
 */
export function getDocumentDisplayIcon(
  input: DocumentIconInput,
  options: DocumentIconOptions = {},
): ReactElement {
  const className = options.className ?? "h-4 w-4"

  if (options.isProjectRoot || resolveKey(input) === "__project_root__") {
    return <FileText className={`${className} text-blue-600`} />
  }

  const key = resolveKey(input)
  const def = kindIconRegistry.get(key) || kindIconRegistry.get("note")!
  return renderDef(def, className)
}

/**
 * Get icon component for a document type.
 * Thin wrapper around getDocumentDisplayIcon for existing call sites.
 */
export function getDocumentIcon(
  type: string,
  className = "h-4 w-4",
  isProjectRoot = false,
) {
  return getDocumentDisplayIcon({ type }, { className, isProjectRoot })
}

/**
 * Detect if a document is a project root file
 * Pattern: {folder}/{folder}.md (e.g., clients/ggia/ggia.md)
 */
export function isProjectRootFile(filePath: string | undefined): boolean {
  if (!filePath) return false

  const pathParts = filePath.split("/").filter((p: string) => p)
  if (pathParts.length < 2) return false

  const folderName = pathParts[pathParts.length - 2].toLowerCase()
  const filenameWithExt = pathParts[pathParts.length - 1]
  const filenameWithoutExt = filenameWithExt
    .replace(/\.(md|markdown)$/i, "")
    .toLowerCase()

  return folderName === filenameWithoutExt
}

/**
 * Get priority emoji for AI suggestions
 */
export function getPriorityIcon(priority: string): string {
  const priorityMap: Record<string, string> = {
    high: "🔥",
    medium: "⭐",
    low: "💡",
  }

  return priorityMap[priority] || "🤖"
}

/**
 * Get collection/category emoji
 */
export function getCollectionIcon(collection: string): string {
  const collectionMap: Record<string, string> = {
    drafts: "📝",
    starred: "⭐",
    templates: "📋",
    archived: "📦",
    recent: "🕐",
  }

  return collectionMap[collection.toLowerCase()] || "📁"
}
